/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react'
import { css } from '@emotion/react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

type MonthYear = {
    year: number
    month: number // 1-12
}

type Props = {
    children: React.ReactElement
    onConfirm: (range: { from: MonthYear; to: MonthYear }) => void
    /** How many years back the year picker exposes. Default: 5. */
    yearsBack?: number
}

const styles = {
    content: css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: 420px;
        padding-top: 8px;
    `,
    row: css`
        display: flex;
        gap: 12px;
    `,
    field: css`
        flex: 1;
    `,
    error: css`
        color: #d32f2f;
        font-size: 12px;
    `,
    help: css`
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
    `,
}

// Returns { year, month } for the calendar month immediately before `now`.
const previousMonth = (now: Date = new Date()): MonthYear => {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
}

const MONTH_KEYS: ReadonlyArray<{ value: number; key: string; fallback: string }> = [
    { value: 1, key: 'TEXT_MONTH_JANUARY', fallback: 'January' },
    { value: 2, key: 'TEXT_MONTH_FEBRUARY', fallback: 'February' },
    { value: 3, key: 'TEXT_MONTH_MARCH', fallback: 'March' },
    { value: 4, key: 'TEXT_MONTH_APRIL', fallback: 'April' },
    { value: 5, key: 'TEXT_MONTH_MAY', fallback: 'May' },
    { value: 6, key: 'TEXT_MONTH_JUNE', fallback: 'June' },
    { value: 7, key: 'TEXT_MONTH_JULY', fallback: 'July' },
    { value: 8, key: 'TEXT_MONTH_AUGUST', fallback: 'August' },
    { value: 9, key: 'TEXT_MONTH_SEPTEMBER', fallback: 'September' },
    { value: 10, key: 'TEXT_MONTH_OCTOBER', fallback: 'October' },
    { value: 11, key: 'TEXT_MONTH_NOVEMBER', fallback: 'November' },
    { value: 12, key: 'TEXT_MONTH_DECEMBER', fallback: 'December' },
]

export const CreateInvoiceDialog: React.FC<Props> = ({ children, onConfirm, yearsBack = 5 }) => {
    const { t } = useTranslation()
    const defaultRange = useMemo(() => {
        const last = previousMonth()
        return { from: last, to: last }
    }, [])

    const [open, setOpen] = useState(false)
    const [from, setFrom] = useState<MonthYear>(defaultRange.from)
    const [to, setTo] = useState<MonthYear>(defaultRange.to)

    const currentYear = new Date().getFullYear()
    const years = useMemo(() => {
        const result: number[] = []
        for (let y = currentYear; y >= currentYear - yearsBack; y--) result.push(y)
        return result
    }, [currentYear, yearsBack])

    // From must not be after To (compared as YYYYMM).
    const fromKey = from.year * 100 + from.month
    const toKey = to.year * 100 + to.month
    const invalid = fromKey > toKey

    const handleClose = () => {
        setOpen(false)
        // Reset to defaults next time it opens.
        setFrom(defaultRange.from)
        setTo(defaultRange.to)
    }

    const handleConfirm = () => {
        if (invalid) return
        onConfirm({ from, to })
        handleClose()
    }

    return (
        <>
            {React.cloneElement(children, { onClick: () => setOpen(true) })}
            <Dialog open={open} onClose={handleClose} maxWidth="sm">
                <DialogTitle>{t('CREATE_INVOICES_HEADER', 'Create invoices')}</DialogTitle>
                <DialogContent>
                    <div css={styles.content}>
                        <div css={styles.help}>
                            {t(
                                'CREATE_INVOICES_RANGE_HELP',
                                'Pick the month range to bill. Both endpoints are inclusive. Defaults to last month.',
                            )}
                        </div>
                        <div>
                            <div css={styles.help}>{t('TEXT_FROM', 'Fra')}</div>
                            <div css={styles.row}>
                                <FormControl css={styles.field} size="small">
                                    <InputLabel>{t('TEXT_MONTH', 'Month')}</InputLabel>
                                    <Select
                                        label={t('TEXT_MONTH', 'Month')}
                                        value={from.month}
                                        onChange={(e) => setFrom({ ...from, month: Number(e.target.value) })}
                                    >
                                        {MONTH_KEYS.map((m) => (
                                            <MenuItem key={m.value} value={m.value}>
                                                {t(m.key, m.fallback)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl css={styles.field} size="small">
                                    <InputLabel>{t('TEXT_YEAR', 'Year')}</InputLabel>
                                    <Select
                                        label={t('TEXT_YEAR', 'Year')}
                                        value={from.year}
                                        onChange={(e) => setFrom({ ...from, year: Number(e.target.value) })}
                                    >
                                        {years.map((y) => (
                                            <MenuItem key={y} value={y}>
                                                {y}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div>
                            <div css={styles.help}>{t('TEXT_TO', 'Til')}</div>
                            <div css={styles.row}>
                                <FormControl css={styles.field} size="small">
                                    <InputLabel>{t('TEXT_MONTH', 'Month')}</InputLabel>
                                    <Select
                                        label={t('TEXT_MONTH', 'Month')}
                                        value={to.month}
                                        onChange={(e) => setTo({ ...to, month: Number(e.target.value) })}
                                    >
                                        {MONTH_KEYS.map((m) => (
                                            <MenuItem key={m.value} value={m.value}>
                                                {t(m.key, m.fallback)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl css={styles.field} size="small">
                                    <InputLabel>{t('TEXT_YEAR', 'Year')}</InputLabel>
                                    <Select
                                        label={t('TEXT_YEAR', 'Year')}
                                        value={to.year}
                                        onChange={(e) => setTo({ ...to, year: Number(e.target.value) })}
                                    >
                                        {years.map((y) => (
                                            <MenuItem key={y} value={y}>
                                                {y}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        {invalid && (
                            <div css={styles.error}>
                                {t(
                                    'CREATE_INVOICES_RANGE_INVALID',
                                    '"From" must be the same month as "To" or earlier.',
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={handleClose}>
                        {t('TEXT_CANCEL', 'AVBRYT')}
                    </Button>
                    <Button color="primary" variant="contained" disabled={invalid} onClick={handleConfirm}>
                        {t('TEXT_CONFIRM', 'BEKREFT')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
